/**
 * ResultRow — one row in the search result list.
 *
 * Renders: kind icon glyph | label (with matched chars highlighted via
 * <mark>) | optional secondary line.
 *
 * Supported kinds: recipe, artefact, batch.
 * Command results have been removed (Track 4.5.F scope-cut). Variations are not
 * a palette kind (2026-07-10 versions-first cleanup).
 *
 * Design refs:
 *   design.md §13a.4 — "icon + label + secondary line; Enter activates; Esc dismisses"
 *   design.md §2 — one accent (focus ring only), hairlines, no shadows
 *   tokens.css §4.2–§4.5 — design tokens used throughout
 *   branding.md §5.2.1 — no banned words; no emoji
 *
 * Accessibility:
 *   role="option" + aria-selected mirrors combobox/listbox pattern.
 *   The parent Palette sets role="listbox" on the containing ul.
 */

import type { CSSProperties } from "react";
import type { PaletteResult } from "@proof/client-core/palette";

// ── CSS (injected once) ───────────────────────────────────────────────────────

const RESULT_ROW_CSS = `
  .proof-palette-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-4);
    cursor: pointer;
    transition: background var(--motion-state);
    min-height: var(--row-height-list);
    border-bottom: 1px solid var(--line);
    user-select: none;
    -webkit-tap-highlight-color: transparent;
  }

  .proof-palette-row:last-child {
    border-bottom: none;
  }

  .proof-palette-row[aria-selected="true"],
  .proof-palette-row:hover {
    background: color-mix(in srgb, var(--text) 5%, transparent);
  }

  .proof-palette-row__icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--muted);
    border: 1px solid var(--line);
    border-radius: var(--radius-button);
  }

  .proof-palette-row__body {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .proof-palette-row__label {
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 400;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .proof-palette-row__label mark {
    color: var(--text);
    background: color-mix(in srgb, var(--accent) 15%, transparent);
    font-weight: 600;
  }

  .proof-palette-row__secondary {
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

`;

let resultRowCssInjected = false;
function injectResultRowCss() {
  if (resultRowCssInjected) return;
  if (typeof document === "undefined") return;
  const id = "proof-palette-row-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = RESULT_ROW_CSS;
    document.head.appendChild(el);
  }
  resultRowCssInjected = true;
}

// ── Kind icon ─────────────────────────────────────────────────────────────────

function kindGlyph(kind: PaletteResult["kind"]): string {
  switch (kind) {
    case "recipe":
      return "R";
    case "artefact":
      return "A";
    case "batch":
      return "B";
  }
}

function kindLabel(kind: PaletteResult["kind"]): string {
  switch (kind) {
    case "recipe":
      return "Recipe";
    case "artefact":
      return "Artefact";
    case "batch":
      return "Batch";
  }
}

// ── Highlight helper ──────────────────────────────────────────────────────────

/**
 * Splits `label` into alternating plain/highlighted segments using the
 * matched char indices from the fzf scorer. Wraps matched chars in <mark>.
 */
function HighlightedLabel({ label, indices }: { label: string; indices: number[] }) {
  if (indices.length === 0) {
    return <>{label}</>;
  }

  const indexSet = new Set(indices);
  const nodes: React.ReactNode[] = [];
  let buffer = "";
  let inMark = false;

  for (let i = 0; i < label.length; i++) {
    const ch = label[i];
    const highlight = indexSet.has(i);

    if (highlight !== inMark) {
      if (buffer.length > 0) {
        if (inMark) {
          nodes.push(<mark key={nodes.length}>{buffer}</mark>);
        } else {
          nodes.push(buffer);
        }
        buffer = "";
      }
      inMark = highlight;
    }
    buffer += ch;
  }

  if (buffer.length > 0) {
    if (inMark) {
      nodes.push(<mark key={nodes.length}>{buffer}</mark>);
    } else {
      nodes.push(buffer);
    }
  }

  return <>{nodes}</>;
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface ResultRowProps {
  result: PaletteResult;
  selected: boolean;
  onActivate: (result: PaletteResult) => void;
  onHover: (result: PaletteResult) => void;
  /** Linear index in the result list, used for id generation. */
  index: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ResultRow({ result, selected, onActivate, onHover, index }: ResultRowProps) {
  injectResultRowCss();

  const label = result.label;
  const matchIndices = result.matchIndices;
  const secondary = "secondary" in result ? result.secondary : null;

  function handleClick() {
    onActivate(result);
  }

  function handleMouseEnter() {
    onHover(result);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate(result);
    }
  }

  return (
    <li
      id={`proof-palette-result-${index}`}
      className="proof-palette-row"
      role="option"
      aria-selected={selected}
      tabIndex={-1}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onKeyDown={handleKeyDown}
    >
      {/* Kind icon */}
      <span
        className="proof-palette-row__icon"
        aria-label={kindLabel(result.kind)}
        title={kindLabel(result.kind)}
      >
        {kindGlyph(result.kind)}
      </span>

      {/* Body: label + secondary */}
      <span className="proof-palette-row__body">
        <span className="proof-palette-row__label">
          <HighlightedLabel label={label} indices={matchIndices} />
        </span>
        {secondary && <span className="proof-palette-row__secondary">{secondary}</span>}
      </span>
    </li>
  );
}

// ── Unused style ref (kept for completeness — removes lint warning) ────────────
const _unusedStyle: CSSProperties = {};
void _unusedStyle;
